import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { JQLSyntaxError } from '@atlassianlabs/jql-ast';
import Bowser from 'bowser';
import mapValues from 'lodash/mapValues';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import getDocumentPosition from '../common/get-document-position';
import { getJastFromState } from '../jql-ast';
import { ERROR_NODE, RICH_INLINE_NODE, SELECTED_NODE } from './constants';
import { richInlineNodes } from './nodes';
import { ReactNodeView } from './util/react-node-view';
var ZERO_WIDTH_SPACE = "\u200B"; // Browsers have very inconsistent ways of handling non-editable content inside of contenteditable elements
// (i.e. rich inline nodes), especially when they are at the end of a block element. This causes all sorts
// of issues when we try to position cursor right after a rich inline node when it is the last element in a
// paragraph. This code adds a widget decoration at the end of each paragraph that meets that condition, with
// a few properties that will allow cursor to be properly displayed across browsers. This is a LARGE hack.
// @see https://bugzilla.mozilla.org/show_bug.cgi?id=1612076
// @see https://bugs.webkit.org/show_bug.cgi?id=50481

var getBrowserSpecificDecorations = function getBrowserSpecificDecorations(state) {
  var decorations = [];
  var browser = Bowser.getParser(window.navigator.userAgent);

  if (browser.getBrowserName() === 'Safari') {
    state.doc.descendants(function (node, pos) {
      if (node.isBlock) {
        var _node$lastChild;

        if (((_node$lastChild = node.lastChild) === null || _node$lastChild === void 0 ? void 0 : _node$lastChild.type.spec.group) === RICH_INLINE_NODE) {
          var position = pos + node.nodeSize - 1;
          var content = document.createTextNode(ZERO_WIDTH_SPACE);
          decorations.push(Decoration.widget(position, content, {
            side: -1,
            key: 'cursor',
            marks: [state.schema.marks.cursor.create()]
          }));
        }
      }

      return false;
    });
  }

  return decorations;
};

var decorateNodesInRange = function decorateNodesInRange(state, decoration, from, to) {
  var decorations = [];
  state.doc.nodesBetween(from, to, function (node, pos) {
    if (node.type.spec.group === RICH_INLINE_NODE) {
      decorations.push(Decoration.node(pos, pos + node.nodeSize, {}, {
        type: decoration
      }));
    }
  });
  return decorations;
};

var RichInlineNodesPluginKey = new PluginKey('rich-inline-nodes-plugin');

var richInlineNodesPlugin = function richInlineNodesPlugin(portalActions) {
  return new Plugin({
    key: RichInlineNodesPluginKey,
    props: {
      decorations: function decorations(state) {
        var decorations = getBrowserSpecificDecorations(state); // Apply selected decoration to node views when they are part of a text selection

        var _state$selection = state.selection,
            from = _state$selection.from,
            to = _state$selection.to;
        decorations.push.apply(decorations, _toConsumableArray(decorateNodesInRange(state, SELECTED_NODE, from, to))); // Apply error decoration to node views when they are part of AST error range

        var ast = getJastFromState(state);

        var _ast$errors = _slicedToArray(ast.errors, 1),
            error = _ast$errors[0];

        if (error instanceof JQLSyntaxError) {
          var documentFrom = getDocumentPosition(state.doc, error.start);
          var documentTo = getDocumentPosition(state.doc, error.stop);
          decorations.push.apply(decorations, _toConsumableArray(decorateNodesInRange(state, ERROR_NODE, documentFrom, documentTo)));
        }

        return DecorationSet.create(state.doc, decorations);
      },
      nodeViews: mapValues(richInlineNodes, function (jqlNodeSpec) {
        return function (node, _view, _getPos, decorations) {
          return ReactNodeView.for(jqlNodeSpec.component, portalActions, node, decorations);
        };
      })
    }
  });
};

export default richInlineNodesPlugin;